// Shared OverflowMenu — a "⋮" button with a short menu, for page headers
// (DEV-51010). Arrow keys, Home and End move between items; Escape closes
// and returns focus to the button; Tab closes and moves on.
import { useEffect, useId, useRef, useState } from 'react'
import { MoreVertical } from 'lucide-react'
import clsx from 'clsx'

export interface OverflowMenuItem {
  label: string
  onSelect: () => void
  destructive?: boolean
}

export interface OverflowMenuProps {
  /** Accessible name for the trigger, e.g. "List options". */
  label: string
  items: OverflowMenuItem[]
  triggerClassName?: string
}

export function OverflowMenu({ label, items, triggerClassName }: OverflowMenuProps) {
  const [open, setOpen] = useState(false)
  const menuId = useId()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([])

  useEffect(() => {
    if (open) itemRefs.current[0]?.focus()
  }, [open])

  const close = (returnFocus: boolean) => {
    setOpen(false)
    if (returnFocus) triggerRef.current?.focus()
  }

  const onMenuKeyDown = (event: React.KeyboardEvent) => {
    const index = itemRefs.current.findIndex((el) => el === document.activeElement)
    const move = (to: number) => {
      event.preventDefault()
      itemRefs.current[(to + items.length) % items.length]?.focus()
    }
    if (event.key === 'ArrowDown') move(index + 1)
    else if (event.key === 'ArrowUp') move(index - 1)
    else if (event.key === 'Home') move(0)
    else if (event.key === 'End') move(items.length - 1)
    else if (event.key === 'Escape') {
      event.preventDefault()
      close(true)
    } else if (event.key === 'Tab') close(false)
  }

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((prev) => !prev)}
        className={clsx(
          'flex h-11 w-11 items-center justify-center rounded-xl transition-colors hover:bg-nav-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary',
          triggerClassName,
        )}
      >
        <MoreVertical className="h-5 w-5" aria-hidden="true" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => close(false)} aria-hidden="true" />
          <div
            id={menuId}
            role="menu"
            aria-label={label}
            onKeyDown={onMenuKeyDown}
            className="absolute right-0 top-full z-40 mt-1 min-w-[180px] rounded-xl border border-gray-200 bg-white py-1 shadow-lg"
          >
            {items.map((item, index) => (
              <button
                key={item.label}
                ref={(el) => {
                  itemRefs.current[index] = el
                }}
                type="button"
                role="menuitem"
                tabIndex={-1}
                onClick={() => {
                  close(true)
                  item.onSelect()
                }}
                className={clsx(
                  'flex min-h-11 w-full items-center px-4 text-left text-sm font-medium hover:bg-gray-50 focus:bg-gray-50 focus:outline-none',
                  item.destructive ? 'text-red-600' : 'text-gray-900',
                )}
              >
                {item.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
